import { FpAxios } from 'fp-components';
import Axios from 'axios';
import { ProspectLossReasonDictionaryType } from '@/types/Dictionaries';
import { BasicDictionary, DictionaryStoreState } from '@/commons/services/store/DictionaryStoreState';
import Partner, { PartnerDTO } from '@/models/Partner';
import { Tag } from '@/models/Discussion';
import DivisionGroup, { DivisionGroupDTO } from '@/models/DivisionGroup';
import { BankBranch, BankDictionaryDTO } from '@/commons/services/models/BankBranchesDictionary';
import { DocumentPeriod } from './models/DocumentPeriod';
import {
  IMultiDocumentCategory,
  MultiDocumentCategory
} from '@/components/checklist/models/MultiDocument/MultiDocumentCategory';
import {
  BackendBankModel,
  BankDictionaryElementDTO,
  financialInstitutions
} from '@/commons/dictionaries/financialInstitutions/FinancialInstitutions';
import {
  BankDictionaryElement,
  mapToBackendBankModel,
  mapToBankDictionaryElementDTO
} from '@/modules/banks/models/BankDictionaryElement';
import {Utils, wait} from '@/services/utils/BasicUtils';
import {District} from '@/commons/services/District';
import {OrganisationUnit} from '@/modules/provisions/models/ProvisionTableDto';
import processProxy from '@/env/processProxy';

type IDictionaryApi = {
  [key in keyof DictionaryStoreState]: () => Promise<any[]>;
}

class DictionaryApi implements IDictionaryApi {
  private dictionaryApi = FpAxios.create();
  private documentAxios = Axios.create({headers: {'X-App-Hash': processProxy.VUE_APP_COMMIT_HASH,},baseURL: processProxy.VUE_APP_DOCUMENT_URL, });

  public async documentValidityPeriods(): Promise<BasicDictionary[]> {
    const response = await this.documentAxios.get<BasicDictionary[]>('document-validity-periods');
    return response.data;
  }

  public async documentIncomeTypes(): Promise<BasicDictionary[]> {
    const response = await this.documentAxios.get<BasicDictionary[]>('dictionaries/income-types');
    return response.data;
  }

  public async documentTypes(): Promise<BasicDictionary[]> {
    const response = await this.documentAxios.get<BasicDictionary[]>('document-types');
    return response.data;
  }

  public async documentPeriods(): Promise<DocumentPeriod[]> {
    const response = await this.documentAxios.get<{ id: number, displayedName: string, }[]>('document-periods');
    return response.data.map(el => new DocumentPeriod(el));
  }

  public async prospectLossReasonDictionary(): Promise<ProspectLossReasonDictionaryType[]> {
    const response = await this.dictionaryApi.get<ProspectLossReasonDictionaryType[]>(`${processProxy.VUE_APP_BASE_URL}dictionaries/loss-reasons`);
    return response.data;
  }

  public async partnerDictionary(): Promise<Partner[]> {
    const response = await this.dictionaryApi.get<Array<PartnerDTO>>('partners');
    return response.data.map((partner: PartnerDTO) => new Partner(partner));
  }

  public async discussionTagsDictionary(): Promise<Tag[]> {
    const response = await this.dictionaryApi.get<Tag[]>('discussions/tags');
    return response.data;
  }

  public async landsDictionary(): Promise<BasicDictionary[]> {
    const response = await this.dictionaryApi.get<BasicDictionary[]>('dictionaries/lands');
    return response.data;
  }

  public async districtsDictionary(): Promise<District[]> {
    const response = await this.dictionaryApi.get<District[]>('dictionaries/districts');
    return response.data;
  }

  public async regionsDictionary(): Promise<{ regionId: number, name: string, id: number }[]> {
    const response = await this.dictionaryApi.get<{ regionId: number, name: string, id: number }[]>('dictionaries/regions');
    return response.data;
  }

  public async divisionsDictionary(): Promise<Array<DivisionGroup>> {
    const response = await this.dictionaryApi.get<Array<DivisionGroupDTO>>('users/divisions-for-filters');
    return response.data.map((divisionDTO: DivisionGroupDTO) => new DivisionGroup(divisionDTO));
  }

  public async multiDocumentCategoryDictionary(): Promise<IMultiDocumentCategory[]> {
    const response = await this.dictionaryApi.get<IMultiDocumentCategory[]>('document-categories');
    return response.data.map(el => new MultiDocumentCategory(el));
  }

  public async bankBranchesDictionary(): Promise<BankBranch[]> {
    const response = await this.dictionaryApi.get<BankDictionaryDTO>('banks');
    const cashBranches = response.data.cash.flatMap(bank => bank.branches
      .map(branch => new BankBranch(branch, 'cash'))).sort((a, b) => a.name.localeCompare(b.name));
    const mortgageBranches = response.data.mortgage.flatMap(bank => bank.branches
      .map(branch => new BankBranch(branch, 'mortgage'))).sort((a, b) => a.name.localeCompare(b.name));
    return cashBranches.concat(mortgageBranches);
  }

  async editBank(bank: BankDictionaryElement): Promise<BankDictionaryElement> {
    const data: BackendBankModel = mapToBackendBankModel(bank);
    const response = await this.dictionaryApi.put<BackendBankModel>(`banks/${bank.id}`, data);
    return new BankDictionaryElement(mapToBankDictionaryElementDTO(response.data));
  }

  async editBankLogo(bankId: number, blob: Blob): Promise<string> {
    const formData = new FormData();
    formData.append('logo', blob);
    const response = await this.dictionaryApi.put<string>(`banks/${bankId}/logo-url`, formData);
    return response.data;
  }

  async changeEnabledBankStatus(bank: BankDictionaryElement): Promise<BankDictionaryElement> {
    bank.enabled = !bank.enabled;
    return this.editBank(bank);
  }

  async addBank(bank: BankDictionaryElement): Promise<BankDictionaryElement> {
    const data: BackendBankModel = mapToBackendBankModel(bank);
    const response = await this.dictionaryApi.post<BackendBankModel>('banks', data);
    return new BankDictionaryElement(mapToBankDictionaryElementDTO(response.data));
  }

  async fetchOrganisationUnits(): Promise<OrganisationUnit[]> {
    const { data, } = await this.dictionaryApi.get<OrganisationUnit[]>('organisation/units');
    return data;
  }

  public async bankDictionary(): Promise<BankDictionaryElement[]> {
    const response = await this.dictionaryApi.get<BackendBankModel[]>('banks/all/simple');
    const mappedResponse: BankDictionaryElementDTO[] = response.data.map(mapToBankDictionaryElementDTO);
    const bankDictionary = mappedResponse.map(x => {
      const index = financialInstitutions.findIndex(institution => institution.id === x.id);
      return Utils.assign(financialInstitutions[index], x);
    });
    return bankDictionary.map(el => new BankDictionaryElement(el))
      .sort((a, b) =>
        a.name_pl.toLowerCase() < b.name_pl.toLowerCase()
          ? -1
          : a.name_pl.toLowerCase() > b.name_pl.toLowerCase()
            ? 1
            : 0
      );
  }
}

export default new DictionaryApi();
